/* global fetch */
/* eslint no-restricted-syntax: "off" */

import events from '../map-events/map-events';

export default class MapModule {
    constructor(params, uniqueId) {
        // TODO: check and output errors if params not provided:
        this.params = {
            ...params,
        };

        this.state = {
            scale: 1,
            translate: {
                x: 0,
                y: 0,
            },
        };

        // When multiple maps in same project:
        this.uniqueId = uniqueId;

        this.addEventsReceivers();

        this.updateState = this.updateState.bind(this);
        this.getState = this.getState.bind(this);
    }

    updateState(newState) {
        // check if any value changed between current and newState:
        const scaleChanged = this.state.scale !== newState.scale;
        const translateXChanged = this.state.translate.x !== newState.translate.x;
        const translateYChanged = this.state.translate.y !== newState.translate.y;

        // if value changed, update state and DOM:
        if (scaleChanged || translateXChanged || translateYChanged) {
            // uddate state:
            this.state.scale = newState.scale;
            this.state.translate.x = newState.translate.x;
            this.state.translate.y = newState.translate.y;

            // update DOM:
            const svgGroup = this.params.rootElement.querySelector('svg .transformBox');
            svgGroup.setAttribute('transform', `scale(${newState.scale})
                translate(${newState.translate.x}, ${newState.translate.y})`);
        }

        // if scale value changed, we already updated state, now we update stroke:
        if (scaleChanged) {
            const newStroke = this.params.stroke / this.state.scale;
            // update DOM:
            const svg = this.params.rootElement.querySelector('svg');
            // check svg size to real size to set accurate stroke width:

            const [, , viewBoxWidth] = svg.getAttribute('viewBox').split(' ');
            const displayRatio = svg.getBoundingClientRect().width / viewBoxWidth;

            svg.style.strokeWidth = `${newStroke / displayRatio}px`;
        }
    }

    getState() {
        return JSON.parse(JSON.stringify(this.state));
    }

    addEventsReceivers() {
        events.on('vj-map', (eventObject) => {
            const { type, content, id } = eventObject;

            if (type === 'select') {
                // check if map has own unique ID:
                if (id === this.uniqueId) {
                    this.zoom.zoomToRegion(content);
                }
            }
            else if (type === 'update-colors') {
                // check if map has own unique ID:
                if (id === this.uniqueId) {
                    this.updateColors(content);
                }
            }
            else if (type === 'region-changed' && this.params.zoomable) {
                // check if map has own unique ID:
                if (id === this.uniqueId) {
                    this.zoom.zoomToRegion(content);
                }
            }
        });
    }

    async init() {
        const mapUrl = this.params.svgUrl;

        const addMouseHoverEvent = (path) => {
            path.addEventListener('mouseenter', () => {
                // highlight the mouseover target
                const pathId = this.getPathId(path);

                events.emit('vj-map', {
                    type: 'mouse-enter',
                    content: pathId,
                    id: this.uniqueId,
                });
            });

            path.addEventListener('mouseleave', () => {
                // highlight the mouseover target
                const pathId = this.getPathId(path);

                events.emit('vj-map', {
                    type: 'mouse-leave',
                    content: pathId,
                    id: this.uniqueId,
                });
            });
        };

        const addMouseClickEvent = (path) => {
            let mouseClicked = false;
            const orginalMousePosition = {
                x: 0,
                y: 0,
            };
            path.addEventListener('mousedown', (event) => {
                mouseClicked = true;
                orginalMousePosition.x = event.clientX;
                orginalMousePosition.y = event.clientY;
            });

            path.addEventListener('mouseup', (event) => {
                event.stopPropagation();
                if (mouseClicked) {
                    mouseClicked = false;
                    const currentMousePos = {
                        x: event.clientX,
                        y: event.clientY,
                    };

                    const deltaPosAbs = {
                        x: Math.abs(currentMousePos.x - orginalMousePosition.x),
                        y: Math.abs(currentMousePos.y - orginalMousePosition.y),
                    };

                    if (deltaPosAbs.x < 5 && deltaPosAbs.y < 5) {
                        const pathId = this.getPathId(path);

                        events.emit('vj-map', {
                            type: 'region-changed',
                            content: pathId,
                            id: this.uniqueId,
                        });
                    }
                    else {
                        // did move, will be a click and drag.
                    }
                }
                return 1;
            });
        };

        return fetch(mapUrl)
            .then(response => response.text())
            .then(svgCode => {
                // add map to DOM
                this.params.rootElement.innerHTML += svgCode;

                const svg = this.params.rootElement.querySelector('svg');

                // debugger;
                // set stroke:
                // check svg size to real size to set accurate stroke width:
                const [, , viewBoxWidth, viewBoxHeight] = svg.getAttribute('viewBox').split(' ');
                const displayRatio = svg.getBoundingClientRect().width / viewBoxWidth;
                svg.style.strokeWidth = `${this.params.stroke / displayRatio}px`;

                // IE fix:
                try {
                    if (window.navigator.userAgent.indexOf('MSIE') > -1 ||
                        window.navigator.appVersion.indexOf('Trident/') > -1) {
                        // IE SVG size fix, manually set height:
                        const viewBoxDimensions = {
                            width: viewBoxWidth,
                            height: viewBoxHeight,
                        };

                        const ratio = viewBoxDimensions.width / viewBoxDimensions.height;
                        svg.style.height = `${svg.getBoundingClientRect().width / ratio}px`;
                    }
                }
                catch (error) {
                    // silent fail.
                }

                // add hover events, colors, etc, to elements:
                const allPaths = Array.from(svg.querySelectorAll('path')).concat(...svg.querySelectorAll('g.region'));
                for (const path of allPaths) {
                    // we only apply to paths with IDs:
                    if (path.id) {
                        const pathId = this.getPathId(path);
                        if (this.params.colors[`${pathId}`]) {
                            path.style.fill = this.params.colors[`${pathId}`];
                            Array.from(path.querySelectorAll('path'))
                                .forEach(p => {
                                    const childPath = p;
                                    childPath.style.fill = this.params.colors[`${pathId}`];
                                });
                        }
                        else if (this.params.colors['default']) {
                            path.style.fill = this.params.colors['default'];
                        }
                    }
                }

                // only load click and drag if map zoomable:
                if (this.params.zoomable) {
                    Promise.all([
                        import('./map-click-drag.js'),
                        import('./map-zoom.js')]).then(([moduleClickDrag, moduleZoom]) => {
                        const ClickAndDragListeners = moduleClickDrag.default;
                        const Zoom = moduleZoom.default;
                        this.zoom = new Zoom(this.params, this.uniqueId, this.getState, this.updateState);
                        new ClickAndDragListeners(this.getState, this.updateState, this.params);

                        allPaths
                            .filter(path => path.querySelectorAll('path').length === 0)
                            .forEach(path => {
                                addMouseHoverEvent(path);
                                addMouseClickEvent(path);
                            });

                        allPaths
                            .filter(path => path.querySelectorAll('path').length > 0)
                            .forEach(path => {
                                const childPaths = Array.from(path.querySelectorAll('path'));
                                childPaths.forEach(p => addMouseHoverEvent(p));
                                childPaths.forEach(p => addMouseClickEvent(p));
                            });

                        events.emit('vj-map', {
                            type: 'loaded',
                            content: '',
                            id: this.uniqueId,
                        });

                        if (this.params.subRegions && this.params.subRegions.size > 0) {
                            import('./map-subregion.js').then((module) => {
                                const SubRegionModule = module.default;
                                this.subRegionModule = new SubRegionModule(
                                    this.params.subRegions,
                                    this.params.rootElement,
                                    this.params.subRegionsSvgUrl,
                                    this.uniqueId,
                                );

                                this.setDefaultRegionId();
                            });
                        }
                        // TODO remove copy/paste (from after import sub-region)
                        else {
                            this.setDefaultRegionId();
                        }
                    });
                }
                else {
                    allPaths
                        .filter(path => path.querySelectorAll('path').length === 0)
                        .forEach(path => {
                            addMouseHoverEvent(path);
                            addMouseClickEvent(path);
                        });

                    allPaths
                        .filter(path => path.querySelectorAll('path').length > 0)
                        .forEach(path => {
                            const childPaths = Array.from(path.querySelectorAll('path'));
                            childPaths.forEach(p => addMouseHoverEvent(p));
                            childPaths.forEach(p => addMouseClickEvent(p));
                        });

                    events.emit('vj-map', {
                        type: 'loaded',
                        content: '',
                        id: this.uniqueId,
                    });

                    this.setDefaultRegionId();
                }
            });
    }

    setDefaultRegionId() {
        const defaultId = this.params.defaultRegionId;
        if (this.params.defaultRegionId !== undefined) {
            if (this.params.zoomable) {
                // check if map has own unique ID:
                this.zoom.zoomToRegion(defaultId);
            }
        }
    }

    regionClicked(path) {
        // region Id:
        const regionId = this.getPathId(path);
        this.zoom.zoomToRegion(regionId);
    }

    updateColors(colors) {
        Object.entries(colors)
            .forEach(([regionId, color]) => {
                const path = this.params.rootElement.querySelector(`
                    svg #${this.params.regionIdPrefix}${regionId} path`);
                if (!path) {
                    return;
                }
                path.style.fill = color;
            });
    }

    getPathId(path) {
        if (this.params.regionIdPrefix) {
            return (path.id).split(this.params.regionIdPrefix)[1];
        }

        if (!path.id) {
            return path.parentNode.id;
        }

        return path.id;
    }
}
